import React from "react"
import type { FallbackProps } from "react-error-boundary"
import { useTranslation } from "react-i18next"

import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined"
import { Button, Link, Stack, Typography } from "@mui/material"

interface ErrorBoundaryContentFallbackProps extends Partial<FallbackProps> {
  /** Fill the viewport or layout main. Omit under page chrome so the header stays. */
  fullScreen?: boolean
}

/**
 * Error fallback sized for the content region below page chrome.
 * Uses a non-h1 heading under page chrome so the page title remains the
 * document heading. Avoids router hooks so it can also render during app boot.
 */
export const ErrorBoundaryContentFallback: React.FC<
  ErrorBoundaryContentFallbackProps
> = ({ fullScreen = false }) => {
  const { t } = useTranslation("common")
  const currentUrl = `${globalThis.location.pathname}${globalThis.location.search}`

  return (
    <Stack
      direction="column"
      role="alert"
      spacing={1}
      sx={{
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        ...(fullScreen
          ? {
              bottom: 0,
              left: 0,
              position: "absolute",
              px: 2,
              right: 0,
              top: 0,
            }
          : {
              flex: 1,
              height: "100%",
              marginBottom: 2,
              minHeight: 0,
            }),
      }}
    >
      <ErrorOutlinedIcon
        aria-hidden="true"
        data-testid="error-boundary-illustration"
        sx={{ color: "neutral.main", fontSize: 120 }}
      />
      <Typography
        align="center"
        component={fullScreen ? "h1" : "h2"}
        variant="h1"
      >
        {t("errorBoundary.contentTitle")}
      </Typography>
      <Typography
        align="center"
        sx={{ color: "text.secondary", pb: 2 }}
        variant="body1"
      >
        {t("errorBoundary.description")}
      </Typography>
      <Button
        component={Link}
        href={currentUrl}
        onClick={(event) => {
          event.preventDefault()
          globalThis.location.reload()
        }}
        variant="contained"
      >
        {t("button.reload")}
      </Button>
    </Stack>
  )
}
